import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, {
  IBrandingApi,
  IColorScheme,
} from '@services/BrandingsService';

import { useEditorMode } from '../EditorModeContext';
import { useDrift } from '../DriftContext';
import { useMapEditorOrNull } from '../MapEditorContext';
import { useIsWalkingLayout } from '../useIsWalkingLayout';
import { setEditorModeInUnreal } from '../mapEditorUnreal';
import { MAP_EDITOR_STATUS_COLORS } from '../MapEditor/styles';
import { useBrandingBarStyles } from './styles';
import QuickSwitcher from './QuickSwitcher';
import DriftSyncSheet from './DriftSyncSheet';

interface BrandingBarProps {
  onOpenLibrary?: () => void;
  onCreateNew?: () => void;
}

/**
 * Renders the editor-mode status pill on the top chrome row: current
 * configuration (opens the QuickSwitcher), autosave state, template drift,
 * live indicator, the way out of a walk, and the exit button.
 *
 * The walk's exit is here rather than on a notice of its own because the two were both centred at
 * the top of the overlay and drew over each other. Merging them also settles which half says what:
 * the status row already answers "is this published or is it your draft", so the control that
 * remains carries only the way back, with the prose the notice used to show moved onto its tooltip.
 *
 * @param props.onOpenLibrary - Called when the QuickSwitcher asks for the full library modal.
 * @param props.onCreateNew - Called when the QuickSwitcher asks for the create flow.
 */
const BrandingBar = ({ onOpenLibrary, onCreateNew }: BrandingBarProps) => {
  const { t: translate } = useTranslation();
  const styles = useBrandingBarStyles();

  const {
    isQuickSwitcherOpen,
    openQuickSwitcher,
    autosaveStatus,
    setWalkRequested,
  } = useEditorMode();
  const { rollup, hasTemplate } = useDrift();
  const [isSyncSheetOpen, setIsSyncSheetOpen] = useState(false);

  // The walk's way out. It used to be a banner of its own, centred at the top of the overlay — the
  // same spot this bar occupies, which is how the two came to be drawn over each other.
  const isWalking = useIsWalkingLayout();

  // Read through the null-tolerant reader rather than useMapEditor: this bar is Editor Mode's shared
  // chrome and is drawn on every focus, while the map-editor draft belongs to one of them. Throwing
  // here would make the map editor's provider a requirement for rendering the bar — the coupling
  // useMapEditorOrNull exists to avoid.
  const mapEditor = useMapEditorOrNull();
  const hasUnpublishedLayout = !!mapEditor?.isDirty;

  const {
    currentRoom,
    currentBrandingId,
    setIsBrandingModeEnabled,
    setRoomSpots,
    setBrandingSpots,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();

  const mapId = currentRoom?.map?.mapId;
  const activeBrandingId = currentRoom?.activeBrandingId;
  const isLive = !!currentBrandingId && currentBrandingId === activeBrandingId;

  const [branding, setBranding] = useState<IBrandingApi | null>(null);
  const brandButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!currentBrandingId || !mapId) {
      setBranding(null);
      return;
    }
    let cancelled = false;
    BrandingsService.getBrandings({ mapId, limit: 100 })
      .then((res) => {
        if (cancelled) {
          return;
        }
        const match =
          res.brandings?.find((b) => b.brandingId === currentBrandingId) ??
          null;
        setBranding(match);
      })
      .catch((loadError) => {
        console.error(
          `Failed to load branding meta: ${(loadError as Error)?.message}`,
          { brandingId: currentBrandingId },
        );
      });
    return () => {
      cancelled = true;
    };
  }, [currentBrandingId, mapId, autosaveStatus]);

  const brandingName = useMemo(() => {
    return branding?.name || translate('adminToolbox.unnamed');
  }, [branding, translate]);

  const handleExit = () => {
    setIsBrandingModeEnabled(false);
    setRoomSpots([]);
    setBrandingSpots([]);
    sendMessageToUnreal({ action: 'LEAVE_CUSTOMIZATION_MODE' });
  };

  const handleBackToPlan = useCallback(() => {
    setWalkRequested(false);
    setEditorModeInUnreal(sendMessageToUnreal, true, 'plan');
  }, [sendMessageToUnreal, setWalkRequested]);

  const renderSwatch = (cs: IColorScheme | undefined) => {
    const hasAny = cs?.primaryColor || cs?.secondaryColor || cs?.outlineColor;
    if (!hasAny) {
      return (
        <Box sx={styles.brandSwatchRow}>
          <Box sx={styles.brandSwatchPlaceholder} />
        </Box>
      );
    }
    return (
      <Box sx={styles.brandSwatchRow}>
        <Box
          sx={[
            styles.brandSwatchStripe,
            { backgroundColor: cs?.primaryColor || 'rgba(255,255,255,0.1)' },
          ]}
        />
        <Box
          sx={[
            styles.brandSwatchStripe,
            { backgroundColor: cs?.secondaryColor || 'rgba(255,255,255,0.1)' },
          ]}
        />
        <Box
          sx={[
            styles.brandSwatchStripe,
            { backgroundColor: cs?.outlineColor || 'rgba(255,255,255,0.1)' },
          ]}
        />
      </Box>
    );
  };

  // One status line for the whole configuration, the map layout included — the layout is saved onto
  // the same branding record as everything else here, so a second status of its own was a second
  // answer to one question. The map editor used to carry one on its Layout plaque, and it read "only
  // you can see this" while this row read "All changes saved": two halves that were each true and
  // contradicted each other on the same screen.
  //
  // The order is not a preference. `saving` and `error` describe a write that is in flight or has
  // just failed, and both clear themselves; an unpublished layout is a standing state that only
  // Publish clears. Showing the draft over a save in flight would hide the write the author started.
  const renderAutosave = () => {
    if (autosaveStatus === 'saving') {
      return (
        <>
          <CircularProgress size={10} sx={{ color: 'rgba(255,255,255,0.6)' }} />
          <span>{translate('editorMode.saving')}</span>
        </>
      );
    }
    if (autosaveStatus === 'error') {
      return (
        <>
          <ErrorOutlineIcon sx={{ fontSize: 12, color: '#FF6B6B' }} />
          <span>{translate('editorMode.saveError')}</span>
        </>
      );
    }
    if (hasUnpublishedLayout) {
      return (
        <Tooltip
          title={translate(
            'editorMode.unpublishedLayoutHint',
            'The walk you are building is not published yet — only you can see it. Publish it under the Scene panel.',
          )}
        >
          <Box sx={styles.autosaveDraft}>
            <Box
              sx={[
                styles.autosaveDot,
                { backgroundColor: MAP_EDITOR_STATUS_COLORS.draftDot },
              ]}
            />
            <span>
              {translate('editorMode.unpublishedLayout', 'Layout draft')}
            </span>
          </Box>
        </Tooltip>
      );
    }

    return (
      <>
        <CheckCircleOutlineIcon sx={{ fontSize: 12, color: '#1DAB56' }} />
        <span>{translate('editorMode.saved')}</span>
      </>
    );
  };

  return (
    <>
      <Box
        sx={styles.bar}
        ref={brandButtonRef as React.RefObject<HTMLDivElement>}
      >
        <Box
          sx={styles.brandButton}
          onClick={openQuickSwitcher}
          data-testid="branding-bar-name-btn"
        >
          {renderSwatch(branding?.brandingColors)}
          <Typography sx={styles.brandName}>{brandingName}</Typography>
          <KeyboardArrowDownIcon
            sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }}
          />
        </Box>

        <Box sx={styles.divider} />
        <Box sx={styles.autosaveRow} data-testid="branding-bar-autosave">
          {renderAutosave()}
        </Box>

        {hasTemplate ? (
          <>
            <Box sx={styles.divider} />
            <Box
              sx={[
                styles.driftPill,
                rollup.state === 'outdated'
                  ? styles.driftPillRed
                  : rollup.state === 'behind'
                  ? styles.driftPillAmber
                  : styles.driftPillGreen,
              ]}
              onClick={() => setIsSyncSheetOpen(true)}
              role="button"
              aria-label={translate('editorMode.driftReview', 'Review drift')}
              data-testid="branding-bar-drift-pill"
            >
              {rollup.state === 'outdated' ? (
                <ErrorOutlineIcon sx={{ fontSize: 15 }} />
              ) : rollup.state === 'behind' ? (
                <SyncOutlinedIcon sx={{ fontSize: 15 }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
              )}
              <Typography sx={styles.driftPillLabel}>
                {rollup.state === 'outdated'
                  ? translate('editorMode.driftOutdated')
                  : rollup.state === 'behind'
                  ? translate('editorMode.driftBehind', {
                      count: rollup.missingSpots + rollup.newScripts,
                    })
                  : translate('editorMode.driftUpToDate')}
              </Typography>
            </Box>
          </>
        ) : null}

        <Box sx={styles.divider} />

        <Box sx={[styles.activeLabel, !isLive && styles.activeLabelInactive]}>
          <Box
            sx={[
              styles.activeLabelDot,
              !isLive && styles.activeLabelDotInactive,
            ]}
          />
          {isLive
            ? translate('adminToolbox.onAir')
            : translate('editorMode.notLive')}
        </Box>

        {isWalking ? (
          <>
            <Box sx={styles.divider} />
            <Tooltip
              title={
                <>
                  <Typography sx={styles.backToPlanTooltipTitle}>
                    {hasUnpublishedLayout
                      ? translate(
                          'editorMode.mapEditorWalkDraftTitle',
                          'Your changes are not saved',
                        )
                      : translate(
                          'editorMode.mapEditorWalkLiveTitle',
                          'You are in the published layout',
                        )}
                  </Typography>
                  <Typography sx={styles.backToPlanTooltipHint}>
                    {hasUnpublishedLayout
                      ? translate(
                          'editorMode.mapEditorWalkDraftHint',
                          'You are standing in them — nobody else can see this yet',
                        )
                      : translate(
                          'editorMode.mapEditorWalkLiveHint',
                          'This is what everyone else is walking through',
                        )}
                  </Typography>
                </>
              }
            >
              <Button
                sx={[
                  styles.backToPlan,
                  hasUnpublishedLayout && styles.backToPlanDraft,
                ]}
                onClick={handleBackToPlan}
                data-testid="map-editor-back-to-plan"
              >
                {translate(
                  'editorMode.mapEditorBackToPlan',
                  'Back to the plan',
                )}
              </Button>
            </Tooltip>
          </>
        ) : null}

        <Tooltip title={translate('adminToolbox.exitEditorMode', 'Exit')}>
          <IconButton
            sx={styles.exitButton}
            onClick={handleExit}
            data-testid="branding-bar-exit-btn"
          >
            <ExitToAppIcon sx={styles.exitIcon} />
          </IconButton>
        </Tooltip>
      </Box>

      <QuickSwitcher
        anchorEl={isQuickSwitcherOpen ? brandButtonRef.current : null}
        onCreateNew={onCreateNew}
        onOpenLibrary={onOpenLibrary}
      />

      <DriftSyncSheet
        open={isSyncSheetOpen}
        onClose={() => setIsSyncSheetOpen(false)}
      />
    </>
  );
};

export default BrandingBar;
